import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CognitoAuthService, UserPermission } from '@techspert-io/auth';
import { IClient } from '@techspert-io/clients';
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
  OfficeLocation,
} from '@techspert-io/shared';
import { ToastService } from '@techspert-io/user-alerts';
import { startWith, Subject, switchMap, takeUntil, takeWhile, tap } from 'rxjs';
import { ITimezone } from '../../../../shared/models/country';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { AppService } from '../../../../shared/services/app.service';
import { CountryService } from '../../../../shared/services/country.service';
import {
  EnrichedUser,
  EnrichedUserCreateTemplate,
  ICreateUser,
  IValidateClientAssignment,
  UserAttributeType,
  UserJobTitle,
} from '../../models/user.models';
import { JobTitlePipe } from '../../pipes/job-title.pipe';
import { UserService } from '../../services/user.service';

type UserForm = FormGroup<{
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  country: FormControl<string>;
  timezone: FormControl<string>;
  clientIds?: FormControl<string[]>;
  [UserAttributeType.JobTitle]?: FormControl<UserJobTitle>;
  [UserAttributeType.OfficeLocation]?: FormControl<OfficeLocation>;
  [UserAttributeType.PhoneNumber]?: FormControl<string>;
  [UserAttributeType.LinkedInURL]?: FormControl<string>;
  [UserAttributeType.MicrosoftID]?: FormControl<string>;
}>;

type AttributeTypeControlNames =
  | UserAttributeType.JobTitle
  | UserAttributeType.OfficeLocation
  | UserAttributeType.PhoneNumber
  | UserAttributeType.LinkedInURL
  | UserAttributeType.MicrosoftID;

@Component({
  selector: 'app-user-form',
  templateUrl: 'user-form.component.html',
  styleUrls: ['user-form.component.scss'],
  imports: [CommonModule, MaterialModule, JobTitlePipe, TitleCasePipe],
})
export class UserFormComponent implements OnChanges, OnDestroy {
  @Input() user: EnrichedUser;
  @Input() clients: IClient[];
  @Input() template: EnrichedUserCreateTemplate;

  @Output() isSubmittingSignal = new EventEmitter();
  @Output() createUserSignal = new EventEmitter();
  @Output() updateUserSignal = new EventEmitter();

  timezones: ITimezone[];

  form: UserForm = new FormGroup({
    firstName: new FormControl(null, [Validators.required]),
    lastName: new FormControl(null, [Validators.required]),
    email: new FormControl(null, [Validators.required, Validators.email]),
    country: new FormControl(null, [Validators.required]),
    timezone: new FormControl(null, [Validators.required]),
  });

  private isSubmittingValue = false;

  private destroy$ = new Subject();

  get isSubmitting() {
    return this.isSubmittingValue;
  }

  set isSubmitting(isSubmitting: boolean) {
    this.isSubmittingValue = isSubmitting;

    if (isSubmitting) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    this.isSubmittingSignal.emit(isSubmitting);
  }

  get canDeactivateUser() {
    return this.auth.loggedInUser.permissions.has(
      UserPermission.UsersDeactivate
    );
  }

  get emailControl() {
    return this.form.controls.email;
  }

  get countryControl() {
    return this.form.controls.country;
  }

  get clientIdsControl() {
    return this.form.controls.clientIds;
  }

  get jobTitleControl() {
    return this.form.controls[UserAttributeType.JobTitle];
  }

  get officeLocationControl() {
    return this.form.controls[UserAttributeType.OfficeLocation];
  }

  get phoneNumberControl() {
    return this.form.controls[UserAttributeType.PhoneNumber];
  }

  get linkedInURLControl() {
    return this.form.controls[UserAttributeType.LinkedInURL];
  }

  get microsoftIdControl() {
    return this.form.controls[UserAttributeType.MicrosoftID];
  }

  get countries() {
    return this.countryService.countryList;
  }

  get jobTitles() {
    return Object.values(UserJobTitle);
  }

  get officeLocations() {
    return Object.values(OfficeLocation);
  }

  constructor(
    private auth: CognitoAuthService,
    private countryService: CountryService,
    private userService: UserService,
    private appService: AppService,
    private toastService: ToastService,
    readonly dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.template && !changes.user) {
      return;
    }

    if (!this.timezones) {
      this.setupTimezoneOptions();
    }

    this.isSubmitting = false;

    if (this.template || this.user) {
      this.setupClientsControl(
        this.template?.client_assignment || this.user?.client_assignment,
        this.user
      );
      this.setupAttributesControls(
        this.template?.attribute_types || this.user?.attribute_types,
        this.user
      );
    }

    this.populateUserData(this.user);
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  submitForm() {
    if (this.form.invalid) {
      return;
    }

    if (this.user) {
      return this.updateUser();
    }

    this.createUser();
  }

  deactivateUser() {
    this.dialog
      .open<ConfirmationDialogComponent, IConfirmationDialogData, boolean>(
        ConfirmationDialogComponent,
        {
          width: '400px',
          minHeight: '150px',
          data: {
            title: 'Deactivate user',
            messages: [
              'This will prevent the user from logging in to any applications.',
              'This action cannot be reversed, do you wish to continue?',
            ],
            isDangerous: true,
          },
        }
      )
      .afterClosed()
      .pipe(
        takeWhile((result) => result),
        switchMap(() => {
          this.isSubmitting = true;
          return this.userService.deactivate(this.user.user_id);
        })
      )
      .pipe(
        tap(() => {
          this.updateUserSignal.emit();
        })
      )
      .subscribe();
  }

  private createUser() {
    this.isSubmitting = true;

    this.userService
      .create(
        this.template.user_create_template_id,
        this.mapFormDataToUserPayload()
      )
      .pipe(
        tap((user) => {
          if (!user) {
            this.isSubmitting = false;
            return;
          }

          this.createUserSignal.emit(user.user_id);
        })
      )
      .subscribe();
  }

  private updateUser() {
    const payload = this.appService.removeUnchangedProps(
      this.mapFormDataToUserPayload(),
      this.user
    );

    if (!Object.keys(payload).length) {
      this.toastService.sendMessage('No changes detected', 'error');
      return;
    }

    this.isSubmitting = true;

    this.userService
      .update({
        user_id: this.user.user_id,
        ...payload,
      })
      .pipe(
        tap((user) => {
          if (!user) {
            this.isSubmitting = false;
            return;
          }

          this.updateUserSignal.emit();
        })
      )
      .subscribe();
  }

  private mapFormDataToUserPayload(): ICreateUser {
    const {
      firstName,
      lastName,
      email,
      country,
      timezone,
      clientIds,
      job_title,
      office_location,
      phone_number,
      linkedin_url,
      microsoft_id,
    } = this.form.getRawValue();

    return {
      email: email.trim().toLowerCase(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      country,
      timezone,
      client_ids: clientIds || [],
      attributes: {
        ...(this.isString(job_title) && {
          [UserAttributeType.JobTitle]: job_title,
        }),
        ...(this.isString(office_location) && {
          [UserAttributeType.OfficeLocation]: office_location,
        }),
        ...(this.isString(phone_number) && {
          [UserAttributeType.PhoneNumber]: phone_number,
        }),
        ...(this.isString(linkedin_url) && {
          [UserAttributeType.LinkedInURL]: linkedin_url,
        }),
        ...(this.isString(microsoft_id) && {
          [UserAttributeType.MicrosoftID]: microsoft_id,
        }),
      },
    };
  }

  private setupTimezoneOptions() {
    this.form.controls.country.valueChanges
      .pipe(
        startWith(''),
        tap((country) => {
          if (!country) {
            return;
          }

          this.timezones = this.countryService.getTimezonesForCountry(
            country,
            false
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setupClientsControl(
    { is_allowed, is_required }: IValidateClientAssignment,
    user?: EnrichedUser
  ) {
    if (!is_allowed) {
      this.form.removeControl('clientIds');
      return;
    }

    this.form.addControl(
      'clientIds',
      new FormControl(
        [],
        is_required ? [Validators.required, Validators.minLength(1)] : []
      )
    );

    if (!user) {
      return;
    }

    this.form.patchValue({
      clientIds: user.client_ids,
    });
  }

  private setupAttributesControls(
    attributeTypes: Partial<Record<UserAttributeType, boolean>>,
    user?: EnrichedUser
  ) {
    const commonAttributeTypeControlNames: AttributeTypeControlNames[] = [
      UserAttributeType.JobTitle,
      UserAttributeType.OfficeLocation,
      UserAttributeType.PhoneNumber,
      UserAttributeType.LinkedInURL,
    ];
    const createAttributeTypeControlNames: AttributeTypeControlNames[] = [
      ...commonAttributeTypeControlNames,
    ];
    const updateAttributeTypeControlNames: AttributeTypeControlNames[] = [
      ...commonAttributeTypeControlNames,
      UserAttributeType.MicrosoftID,
    ];

    (user
      ? updateAttributeTypeControlNames
      : createAttributeTypeControlNames
    ).forEach((attributeType) => {
      if (!(attributeType in attributeTypes)) {
        this.form.removeControl(attributeType);
        return;
      }

      this.form.addControl(
        attributeType as
          | UserAttributeType.JobTitle
          | UserAttributeType.OfficeLocation
          | UserAttributeType.PhoneNumber
          | UserAttributeType.LinkedInURL
          | UserAttributeType.MicrosoftID,
        new FormControl(
          null,
          attributeTypes[attributeType] ? [Validators.required] : []
        )
      );

      if (!user) {
        return;
      }

      this.form.patchValue({
        [attributeType]: user.attributes[attributeType],
      });
    });
  }

  private populateUserData(user?: EnrichedUser) {
    if (!user) {
      return;
    }

    this.form.patchValue({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      country: user.country,
      timezone: user.timezone,
    });

    this.emailControl.disable();

    if (!user.is_active) {
      this.form.disable();
    }
  }

  private isString(value: unknown) {
    return typeof value === 'string';
  }
}
